import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Select, message, Layout } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import api from '../utils/api';
import { Content, Header } from 'antd/es/layout/layout';
import TopNav from './TopNav';
import useSetTitle from './GlobalSetTitleHook';

const { Option } = Select;

interface Lab {
  id: number;
  name: string;
}

interface User {
  id: number;
  username: string;
  email: string;
  labs: Lab[];
  lab_ids?: number[];
  is_active: boolean;
  is_staff: boolean;
  is_superuser: boolean;
}

interface UserResponse {
  data: User[];
  totalCount: number;
}

const UserManager: React.FC = () => {
  useSetTitle();
  const [users, setUsers] = useState<User[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [labs, setLabs] = useState<Lab[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchUsers();
    fetchLabs();
  }, []);

  const handlePageChange = async (page: number, pageSize: number) => {
    setPage(page);
    setPageSize(pageSize);
    fetchUsers(page, pageSize);
  };

  const fetchUsers = async (p?: number, ps?: number) => {
    try {
      const res = await api.get(`/api/users/?page=${p || page}&size=${ps || pageSize}`);
      setUsers(res.data?.data);
      setTotal(res.data?.totalCount);
    } catch (error) {
      message.error('获取用户列表失败');
    }
  };

  const fetchLabs = async () => {
    try {
      const res = await api.get<Lab[]>('/api/lab/');
      setLabs(res.data);
    } catch (error) {
      message.error('获取实验室列表失败');
    }
  };

  const handleUpdateUser = async (values: any) => {
    try {
      if (!editingUser) return;
        await api.patch(`/api/users/${editingUser.id}/`, {
        lab_ids: values.lab_ids,
      });
      message.success('更新成功');
      setModalVisible(false);
      fetchUsers(page, pageSize);
    } catch (error) {
      message.error('更新失败');
    }
  };

const columns = [
  { title: '用户名', dataIndex: 'username', key: 'username' },
  { title: '姓名', dataIndex: 'last_name', key: 'last_name' },
  { title: '邮箱', dataIndex: 'email', key: 'email' },
  {
    title: '所属实验室',
    key: 'labs',
    render: (_: any, record: User) =>
      record.labs && record.labs.length > 0
        ? record.labs.map((lab) => lab.name).join(', ')
        : '-',
  },
  {
    title: 'Staff',
    dataIndex: 'is_staff',
    key: 'is_staff',
    render: (v: boolean) => (v ? '是' : '否'),
  },
  {
    title: 'Active',
    dataIndex: 'is_active',
    key: 'is_active',
    render: (v: boolean) => (v ? '是' : '否'),
  },
  {
    title: '操作',
    key: 'action',
    render: (_: any, record: User) => (
      <Button
        type="link"
        icon={<EditOutlined />}
        onClick={() => {
          setEditingUser(record);
          form.setFieldsValue({
            lab_ids: record.labs?.map((lab) => lab.id),
          });
          setModalVisible(true);
        }}
      >
        编辑实验室
      </Button>
    ),
  },
];


  return ( <Layout className="layout">
  <Header style={{ background: '#f0f2f5', padding: 0 }}> <TopNav /> </Header> <Content>
  <div style={{ padding: '20px' }}> <h2>用户管理</h2>
  <Table
  columns={columns}
  dataSource={users}
  rowKey="id"
  pagination={{ pageSize: pageSize, current: page, total: total, onChange: handlePageChange}}
  />

  ```
        <Modal
          title={editingUser ? `编辑用户：${editingUser.username}` : '编辑用户'}
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          onOk={() => form.submit()}
        >
          <Form form={form} onFinish={handleUpdateUser} layout="vertical">
            <Form.Item
              label="实验室"
              name="lab_ids"
              rules={[{ required: true, message: '请选择实验室' }]}
            >
              <Select mode="multiple" placeholder="选择实验室">
                {labs.map((lab) => (
                  <Option key={lab.id} value={lab.id}>
                    {lab.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </Content>
  </Layout>
  );
};

export default UserManager;
